/**
 * Handler for audit logging functionality
 * @returns {Object} Object containing audit logging methods
 */
const auditLoggingHandler = function () {
  /**
   * Compares two values and returns their differences in a structured format
   * @param {*} prev - Previous value
   * @param {*} next - New value
   * @returns {Object} Object containing the differences between prev and next
   */
  const getAuditChanges = function (prev: any, next: any): Record<string, any> {
    // Handle null/undefined cases
    if (!prev || !next) {
      return next;
    }

    const changes: any = {};

    // Handle array comparison
    if (Array.isArray(prev) && Array.isArray(next)) {
      if (JSON.stringify(prev) !== JSON.stringify(next)) {
        return next;
      }
      return changes;
    }

    // Handle primitive types (string, number, boolean)
    if (typeof prev !== "object" || typeof next !== "object") {
      if (prev !== next) {
        return next;
      }
      return changes;
    }

    // Handle objects
    Object.keys({ ...prev, ...next }).forEach(function (key) {
      const prevValue = prev[key];
      const nextValue = next[key];

      // Skip if values are identical
      if (JSON.stringify(prevValue) === JSON.stringify(nextValue)) return;

      // Handle nested objects/arrays
      if (typeof prevValue === "object" && typeof nextValue === "object") {
        const nestedChanges = getAuditChanges(prevValue, nextValue);
        if (Object.keys(nestedChanges).length > 0) {
          if (Array.isArray(prevValue) || !isNestedObject(prevValue)) {
            changes[key] = nextValue;
          } else {
            // For nested objects, maintain the structure
            changes[key] = {
              ...changes[key],
              ...nestedChanges,
            };
          }
        }
      } else if (prevValue !== nextValue) {
        changes[key] = nextValue;
      }
    });

    return changes;
  };

  /**
   * Checks if a value is a nested object (not an array or null)
   * @param {*} value - Value to check
   * @returns {boolean} True if value is a nested object
   */
  const isNestedObject = function (value: any): boolean {
    return typeof value === "object" && !Array.isArray(value) && value !== null;
  };

  /**
   * Flattens a nested changes object into a single-level object with dot notation keys
   * @param {Object} changes - The changes object to flatten
   * @param {string} prefix - Prefix for nested keys
   * @returns {Object} Flattened object
   */
  const flattenChanges = function (
    changes: Record<string, any>,
    prefix: string = "",
  ): Record<string, any> {
    let flattened: any = {};

    Object.keys(changes).forEach(function (key) {
      const value = changes[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value.prev !== undefined && value.next !== undefined) {
        flattened[newKey] = value;
      } else if (typeof value === "object") {
        Object.assign(flattened, flattenChanges(value, newKey));
      }
    });

    return flattened;
  };

  const getAuditChangesObject = function (prev: any, next: any): any {
    const changes = getAuditChanges(prev, next);
    console.table({ prev, next, changes });

    return changes;
  };

  return {
    getAuditChangesObject,
  };
};

export default auditLoggingHandler;
